import { useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  Bracket,
  Seed,
  SeedItem,
  SeedTeam,
} from '@apex467/react-brackets'
import type { IRoundProps, IRenderSeedProps } from '@apex467/react-brackets'
import { TournamentGame, TournamentTeam } from '../services/api'
import { formatEventDateParts } from '../utils/timezone'
import { useTeamDisplay, deriveTeamCode } from '../utils/teamDisplay'
import VenueLink from './VenueLink'
import { useLabels, STORED_PLAY_IN_SLOT_PREFIX } from '../site/core/labels'
import { useT } from '@/i18n'
import { siteCoreMessages } from '@/i18n/catalogs/site-core'

interface ScheduleBracketProps {
  games: TournamentGame[]
  teams: TournamentTeam[]
  autoAdvance?: boolean
  /** Compact card style for embedding in the homepage widget slot.
   *  Strips venue, time, and the View link; shrinks cards + logos so
   *  the whole bracket fits a narrow container with much less scroll. */
  compact?: boolean
}

interface BracketTeamData {
  id: number | null
  name: string
  fullName: string
  logo: string | null
  score: number
  isWinner: boolean
  isTBD: boolean
  // When team_id is NULL but the row carries a slot label ("Winner of
  // Play-In G1", "Lowest remaining seed"), fans see the label italicized
  // + muted instead of the generic "TBD" fallback.
  slotLabel?: string | null
}

/** A bracket-library round plus its `round_number` (0 = play-in), so logic
 *  never has to key on the display title. */
type BracketRound = IRoundProps & { roundNumber: number }

// A bracket game shows its score only once it's been tipped off, and a live
// game only while it has one: a game hidden from fans comes without (#1280).
function hasScore(game: TournamentGame | undefined): boolean {
  if (game?.status === 'completed') return true
  return game?.status === 'in_progress' && game.home_score != null && game.away_score != null
}

export default function ScheduleBracket({ games, teams, autoAdvance = true, compact = false }: ScheduleBracketProps) {
  const navigate = useNavigate()
  const teamName = useTeamDisplay()
  const labels = useLabels()
  const w = useT(siteCoreMessages).widgets.bracket
  const tbd = labels.ui.common.tbd
  const liveText = labels.status({ status: 'in_progress' }, { variant: 'compact' }).text

  // Convert games to bracket library format
  const rounds: BracketRound[] = useMemo(() => {
    // Group games by round
    const gamesByRound = new Map<number, TournamentGame[]>()
    games.forEach((game) => {
      if (!gamesByRound.has(game.round_number)) {
        gamesByRound.set(game.round_number, [])
      }
      gamesByRound.get(game.round_number)!.push(game)
    })

    // Sort games within each round
    gamesByRound.forEach((roundGames) => {
      roundGames.sort((a, b) => a.match_number - b.match_number)
    })

    const roundNumbers = Array.from(gamesByRound.keys()).sort((a, b) => a - b)
    // Play-in (round 0) doesn't count toward the semantic label math — so
    // an R16 bracket with a play-in still labels rounds R16/QF/SF/F.
    const semanticRoundNumbers = roundNumbers.filter((r) => r >= 1)
    const numRounds = semanticRoundNumbers.length

    // Names come from the shared label layer; the rounds left are counted
    // by position so gapped round numbers still name correctly.
    const getRoundName = (roundNum: number): string =>
      labels.roundName(roundNum, numRounds, roundNum === 0 ? undefined : numRounds - semanticRoundNumbers.indexOf(roundNum))

    // Convert to bracket format
    return roundNumbers.map((roundNum) => {
      const roundGames = gamesByRound.get(roundNum) || []

      return {
        title: getRoundName(roundNum),
        // Logic keys on the round number, never on the title text.
        roundNumber: roundNum,
        seeds: roundGames.map((game) => {
          const getTeamDisplay = (
            teamId: number | null,
            teamName: string | null,
            teamCode: string | null,
            teamLogo: string | null,
            slotLabel: string | null | undefined
          ) => {
            if (teamId === null) {
              const label = slotLabel && slotLabel.trim() !== '' ? slotLabel : tbd
              return { name: label, code: label, logo: null, id: null, slotLabel: slotLabel ?? null }
            }
            const team = teams.find((t) => t.team_id === teamId)
            return {
              name: teamName || team?.name || w.unknownTeam,
              code: teamCode || team?.short_code || (teamName ? deriveTeamCode(teamName) : '???'),
              logo: teamLogo || team?.logo_url || null,
              id: teamId,
              slotLabel: null,
            }
          }

          const home = getTeamDisplay(
            game.home_team_id,
            game.home_team_name,
            game.home_team_code,
            game.home_team_logo,
            game.home_slot_label
          )
          const away = getTeamDisplay(
            game.away_team_id,
            game.away_team_name,
            game.away_team_code,
            game.away_team_logo,
            game.away_slot_label
          )

          const isCompleted = game.status === 'completed'
          const homeWins = isCompleted && (game.home_score ?? 0) > (game.away_score ?? 0)
          const awayWins = isCompleted && (game.away_score ?? 0) > (game.home_score ?? 0)

          return {
            id: game.id,
            date: game.game_date || undefined,
            teams: [
              {
                id: home.id,
                name: home.code,
                fullName: home.name,
                logo: home.logo,
                score: game.home_score ?? undefined,
                isWinner: homeWins,
                isTBD: home.id === null,
                slotLabel: home.slotLabel,
              },
              {
                id: away.id,
                name: away.code,
                fullName: away.name,
                logo: away.logo,
                score: game.away_score ?? undefined,
                isWinner: awayWins,
                isTBD: away.id === null,
                slotLabel: away.slotLabel,
              },
            ],
            gameData: game,
            isPlayIn: roundNum === 0,
          }
        }),
      }
    })
  }, [games, teams, labels, tbd, w.unknownTeam])

  const handleGameClick = (gameId: number) => {
    navigate(`/game/${gameId}`)
  }

  // Custom seed/match rendering component
  const CustomSeed = ({ seed, breakpoint }: IRenderSeedProps) => {
    const gameData = (seed as { gameData?: TournamentGame }).gameData
    const isPlayIn = !!(seed as { isPlayIn?: boolean }).isPlayIn
    const seedTeams = (seed.teams || []) as unknown as BracketTeamData[]
    const defaultTeam: BracketTeamData = { id: null, name: tbd, fullName: tbd, logo: null, score: 0, isWinner: false, isTBD: true, slotLabel: null }
    const homeTeam = seedTeams[0] || defaultTeam
    const awayTeam = seedTeams[1] || defaultTeam

    const isLive = gameData?.status === 'in_progress'
    const homeHasLabel = homeTeam.isTBD && !!homeTeam.slotLabel
    const awayHasLabel = awayTeam.isTBD && !!awayTeam.slotLabel

    // Compact mode strips venue + time + View link and shrinks
    // everything so the whole bracket fits inside a small homepage
    // widget slot without disappearing horizontally. Cards drop
    // 220 → 130px, logos 24 → 16px, font 14 → 11px. Winner check,
    // slot labels, and the play-in tint all stay so the visual
    // grammar reads the same as the full page.
    const rowPad = compact ? '4px 6px' : '10px 12px'
    const logoBoxCls = compact ? 'w-4 h-4 text-[7px]' : 'w-6 h-6 text-[9px]'
    const teamGap = compact ? 'gap-1.5' : 'gap-2'
    const cardWidth = compact ? '130px' : '220px'
    const seedFontPx = compact ? 11 : 14
    const bothZero = (homeTeam.score ?? 0) === 0 && (awayTeam.score ?? 0) === 0
    // Scores only mean something once a game is live or final — an
    // unplayed game would otherwise read "TBD 0 / TBD 0" (#199). The
    // compact widget additionally hides a 0–0 to reduce noise.
    const showScores = hasScore(gameData) && !(compact && bothZero)
    return (
      <Seed mobileBreakpoint={breakpoint} style={{ fontSize: seedFontPx }}>
        <SeedItem
          style={{
            // Dark glass card; light themes swap it via --bracket-seed-bg (#193).
            backgroundColor: isPlayIn ? 'rgba(184, 84, 26, 0.10)' : 'var(--bracket-seed-bg, rgba(7, 21, 15, 0.45))',
            border: isLive
              ? '1px solid rgba(255, 215, 0, 0.5)'
              : isPlayIn
              ? '1px dashed rgba(255, 200, 150, 0.30)'
              : '1px solid rgba(26, 71, 42, 0.25)',
            borderRadius: compact ? '5px' : '8px',
            padding: 0,
            cursor: 'pointer',
            width: cardWidth,
            backdropFilter: 'blur(8px)',
          }}
          onClick={() => gameData && handleGameClick(gameData.id)}
        >
          {/* Live indicator — full pill on regular, thin bar on compact */}
          {isLive && !compact && (
            <div className="px-3 py-1 bg-primary/10 border-b border-primary/20 flex items-center justify-center gap-2">
              <span className="w-2 h-2 rounded-full bg-primary animate-pulse" />
              <span className="text-primary text-xs font-bold uppercase">{liveText}</span>
            </div>
          )}
          {isLive && compact && (
            <div className="h-[3px] bg-primary/70" />
          )}

          {/* Home team */}
          <SeedTeam
            style={{
              backgroundColor: homeTeam.isWinner ? 'rgba(34, 197, 94, 0.1)' : 'transparent',
              padding: rowPad,
              borderBottom: '1px solid rgba(255, 255, 255, 0.05)',
              opacity: homeTeam.isTBD ? 0.5 : 1,
            }}
          >
            <div className={`flex items-center ${teamGap} flex-1 min-w-0`}>
              {homeTeam.logo ? (
                <img src={homeTeam.logo} alt="" className={`${logoBoxCls.split(' ')[0]} ${logoBoxCls.split(' ')[1]} object-contain rounded`} />
              ) : (
                <div className={`${logoBoxCls} rounded bg-white/10 flex items-center justify-center font-bold text-white/40`}>
                  {deriveTeamCode(homeTeam.fullName || homeTeam.name || 'TBD')}
                </div>
              )}
              {homeHasLabel ? (
                <span className="italic text-white/60 font-medium truncate">
                  {homeTeam.fullName || homeTeam.name}
                </span>
              ) : (
                <span className={`font-bold truncate ${homeTeam.isWinner ? 'text-green-400' : 'text-white'}`}>
                  {teamName({ name: homeTeam.fullName || homeTeam.name || 'TBD', short_code: homeTeam.name }, 'auto')}
                </span>
              )}
              {homeTeam.isWinner && !compact && <span className="text-green-400 text-xs">✓</span>}
            </div>
            {showScores && (
              <span className={`font-bold tabular-nums ${homeTeam.isWinner ? 'text-green-400' : 'text-white/60'}`}>
                {homeTeam.score ?? 0}
              </span>
            )}
          </SeedTeam>

          {/* Away team */}
          <SeedTeam
            style={{
              backgroundColor: awayTeam.isWinner ? 'rgba(34, 197, 94, 0.1)' : 'transparent',
              padding: rowPad,
              opacity: awayTeam.isTBD ? 0.5 : 1,
            }}
          >
            <div className={`flex items-center ${teamGap} flex-1 min-w-0`}>
              {awayTeam.logo ? (
                <img src={awayTeam.logo} alt="" className={`${logoBoxCls.split(' ')[0]} ${logoBoxCls.split(' ')[1]} object-contain rounded`} />
              ) : (
                <div className={`${logoBoxCls} rounded bg-white/10 flex items-center justify-center font-bold text-white/40`}>
                  {deriveTeamCode(awayTeam.fullName || awayTeam.name || 'TBD')}
                </div>
              )}
              {awayHasLabel ? (
                <span className="italic text-white/60 font-medium truncate">
                  {awayTeam.fullName || awayTeam.name}
                </span>
              ) : (
                <span className={`font-bold truncate ${awayTeam.isWinner ? 'text-green-400' : 'text-white'}`}>
                  {teamName({ name: awayTeam.fullName || awayTeam.name || 'TBD', short_code: awayTeam.name }, 'auto')}
                </span>
              )}
              {awayTeam.isWinner && !compact && <span className="text-green-400 text-xs">✓</span>}
            </div>
            {showScores && (
              <span className={`font-bold tabular-nums ${awayTeam.isWinner ? 'text-green-400' : 'text-white/60'}`}>
                {awayTeam.score ?? 0}
              </span>
            )}
          </SeedTeam>

          {/* Game info footer — omitted entirely in compact. The widget
              context doesn't have room for venue/time; the whole card
              is still clickable so a per-card "View" arrow is redundant. */}
          {!compact && (
            <div className="px-3 py-2 border-t border-secondary/20 bg-bg-primary/20">
              <div className="flex items-center justify-between gap-1 flex-wrap text-[10px]">
                <div className="flex items-center gap-2 text-white/40">
                  {gameData?.game_date && (() => {
                    const { date, time, tzAbbrev } = formatEventDateParts(gameData.game_date, gameData.timezone)
                    return <span>{date}, {time}{tzAbbrev ? ` ${tzAbbrev}` : ''}</span>
                  })()}
                  {gameData?.venue_name && (
                    <>
                      <span>•</span>
                      <VenueLink
                        venueId={gameData.venue_id}
                        venueName={gameData.venue_name}
                        className="truncate max-w-[100px]"
                      />
                    </>
                  )}
                </div>
                <span className="text-primary/60 hover:text-primary transition-colors flex items-center gap-1">
                  {w.view}
                  <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                </span>
              </div>
            </div>
          )}
        </SeedItem>
      </Seed>
    )
  }

  if (games.length === 0) {
    return (
      <div className="bg-bg-secondary/45 backdrop-blur-md border border-secondary/25 rounded-lg p-8 text-center">
        <div className="text-primary text-4xl mb-4">🏆</div>
        <h3 className="text-lg font-bold text-white mb-2">{labels.ui.schedule.bracketEmptyTitle}</h3>
        <p className="text-white/60">{labels.ui.schedule.bracketEmptyBody}</p>
      </div>
    )
  }

  // Flat round-by-round view for non-auto-advance tournaments. The `rounds`
  // memo already sorts play-in (round 0) first and titles it, so we just use
  // its titles here rather than recomputing.
  if (!autoAdvance) {
    return (
      // In the compact homepage widget the round-by-round grid can run taller
      // than the section slot, so cap it and scroll internally. The full
      // /schedule page (compact=false) keeps the natural, uncapped height.
      <div className={compact ? 'space-y-3 max-h-[440px] overflow-y-auto scrollbar-hide' : 'space-y-6'}>
        {rounds.map((round, roundIdx) => {
          const roundName = round.title
          const isPlayInRound = round.roundNumber === 0
          return (
            <div
              key={roundIdx}
              className={`backdrop-blur-md border rounded-lg overflow-hidden ${
                isPlayInRound
                  ? 'bg-orange-500/[0.04] border-orange-400/25'
                  : 'bg-bg-secondary/45 border-secondary/25'
              }`}
            >
              <div className="px-4 py-3 border-b border-secondary/20">
                <h4 className="text-sm font-bold text-primary uppercase tracking-wider">{roundName}</h4>
              </div>
              <div className="p-4 grid grid-cols-1 sm:grid-cols-2 gap-3">
                {round.seeds.map((seed) => {
                  const gameData = (seed as { gameData?: TournamentGame }).gameData
                  const seedTeams = (seed.teams || []) as unknown as BracketTeamData[]
                  const defaultTeam: BracketTeamData = { id: null, name: tbd, fullName: tbd, logo: null, score: 0, isWinner: false, isTBD: true, slotLabel: null }
                  const homeTeam = seedTeams[0] || defaultTeam
                  const awayTeam = seedTeams[1] || defaultTeam
                  const isLive = gameData?.status === 'in_progress'
                  const showScores = hasScore(gameData)
                  return (
                    <div
                      key={seed.id}
                      onClick={() => gameData && handleGameClick(gameData.id)}
                      className={`border rounded-lg overflow-hidden cursor-pointer transition-all hover:border-primary/30 ${isLive ? 'border-primary/50 bg-primary/5' : 'border-secondary/25 bg-bg-primary/20'}`}
                    >
                      {isLive && (
                        <div className="px-3 py-1 bg-primary/10 border-b border-primary/20 flex items-center justify-center gap-2">
                          <span className="w-2 h-2 rounded-full bg-primary animate-pulse" />
                          <span className="text-primary text-xs font-bold uppercase">{liveText}</span>
                        </div>
                      )}
                      {[homeTeam, awayTeam].map((team, i) => {
                        const hasLabel = team.isTBD && !!team.slotLabel
                        return (
                        <div key={i} className={`flex items-center justify-between px-3 py-2.5 ${i === 0 ? 'border-b border-white/5' : ''} ${team.isWinner ? 'bg-green-500/10' : ''}`}>
                          <div className="flex items-center gap-2 min-w-0">
                            {team.logo ? (
                              <img src={team.logo} alt="" className="w-6 h-6 object-contain rounded" />
                            ) : (
                              <div className="w-6 h-6 rounded bg-white/10 flex items-center justify-center text-[9px] font-bold text-white/40">{deriveTeamCode(team.fullName || team.name || 'TBD')}</div>
                            )}
                            {hasLabel ? (
                              <span className="italic text-white/50 font-medium truncate text-sm">
                                {team.fullName || team.name}
                              </span>
                            ) : (
                              <span className={`font-bold truncate text-sm ${team.isWinner ? 'text-green-400' : team.isTBD ? 'text-white/40' : 'text-white'}`}>{teamName({ name: team.fullName || team.name || 'TBD', short_code: team.name }, 'auto')}</span>
                            )}
                            {team.isWinner && <span className="text-green-400 text-xs">✓</span>}
                          </div>
                          {showScores && (
                            <span className={`font-bold tabular-nums ${team.isWinner ? 'text-green-400' : 'text-white/60'}`}>{team.score ?? 0}</span>
                          )}
                        </div>
                        )
                      })}
                      {/* Date, time, court */}
                      {(gameData?.game_date || gameData?.venue_name) && (
                        <div className="px-3 py-2 border-t border-secondary/20 bg-bg-primary/20 flex items-center justify-between text-[10px] text-white/40">
                          <div className="flex items-center gap-2">
                            {gameData.game_date && (() => {
                              const { date, time, tzAbbrev } = formatEventDateParts(gameData.game_date, gameData.timezone)
                              return <span>{date}, {time}{tzAbbrev ? ` ${tzAbbrev}` : ''}</span>
                            })()}
                            {gameData.venue_name && (
                              <>
                                {gameData.game_date && <span>·</span>}
                                <VenueLink
                                  venueId={gameData.venue_id}
                                  venueName={gameData.venue_name}
                                  parentVenueId={gameData.parent_venue_id}
                                  parentVenueName={gameData.parent_venue_name}
                                />
                              </>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  )
                })}
              </div>
            </div>
          )
        })}
      </div>
    )
  }

  // Detect manual play-in mode from the STORED slot labels. The public API
  // doesn't expose next_game_id, but locked mode auto-populates R1 empty
  // slots with "Winner of Play-In G{N}" labels (written by the backend
  // scheduler; data, not display wording) — so if a round-0 exists and R1
  // has NO such labels, the tournament is in manual mode. In manual mode
  // we split the render into two Bracket components so the library's
  // auto connectors between round 0 and round 1 don't imply a fixed
  // pairing that doesn't exist.
  const hasPlayIn = games.some((g) => g.round_number === 0)
  const anyLockedR1Label = games.some(
    (g) =>
      g.round_number === 1 &&
      ((g.home_slot_label || '').startsWith(STORED_PLAY_IN_SLOT_PREFIX) ||
        (g.away_slot_label || '').startsWith(STORED_PLAY_IN_SLOT_PREFIX))
  )
  const isManualPlayIn = hasPlayIn && !anyLockedR1Label
  const playInRounds = rounds.filter((r) => r.roundNumber === 0)
  const mainRounds = rounds.filter((r) => r.roundNumber !== 0)

  const renderTitle = (title: string) => (
    <div className={compact ? 'text-center mb-2' : 'text-center mb-4'}>
      <h4 className={compact
        ? 'text-[9px] font-bold text-primary/70 uppercase tracking-wider'
        : 'text-sm font-bold text-primary uppercase tracking-wider'}>
        {title}
      </h4>
    </div>
  )

  const bracketStyles = (
    <style>{`
      .bracket-public {
        gap: ${compact ? 16 : 40}px;
      }
      .bracket-public svg {
        stroke: rgba(26, 71, 42, 0.5) !important;
      }
      /* react-brackets draws its connector lines as ::before and ::after
         pseudo-elements on the Seed wrapper (a styled-component). Hide
         them for the standalone play-in section so the split rendering
         reads as two separate stages, not a broken bracket. Also blank
         out any bordered pseudos as a belt-and-braces fallback. */
      .bracket-public-playin [mobilebreakpoint]::before,
      .bracket-public-playin [mobilebreakpoint]::after {
        content: none !important;
        display: none !important;
        border: 0 !important;
      }
      .round-public {
        gap: ${compact ? 10 : 24}px;
      }
    `}</style>
  )

  const showSplit = isManualPlayIn && playInRounds.length > 0 && mainRounds.length > 0

  return (
    <div className={compact ? '' : 'space-y-6'}>
      <div className={compact
        ? 'overflow-x-auto'
        : 'bg-bg-secondary/45 backdrop-blur-md border border-secondary/25 rounded-lg p-4 overflow-x-auto'}>
        {showSplit ? (
          <div className={`flex flex-row items-start ${compact ? 'gap-3' : 'gap-8'}`}>
            <Bracket
              rounds={playInRounds}
              renderSeedComponent={CustomSeed}
              bracketClassName="bracket-public bracket-public-playin"
              roundClassName="round-public"
              mobileBreakpoint={0}
              roundTitleComponent={renderTitle}
            />
            <Bracket
              rounds={mainRounds}
              renderSeedComponent={CustomSeed}
              bracketClassName="bracket-public"
              roundClassName="round-public"
              mobileBreakpoint={0}
              roundTitleComponent={renderTitle}
            />
          </div>
        ) : (
          <Bracket
            rounds={rounds}
            renderSeedComponent={CustomSeed}
            bracketClassName="bracket-public"
            roundClassName="round-public"
            mobileBreakpoint={0}
            roundTitleComponent={renderTitle}
          />
        )}
        {bracketStyles}
      </div>

      {/* Legend + mobile helper — hidden in compact widget mode where
          the caller (BracketWidget) supplies its own CTA/hint. */}
      {!compact && (
        <>
          <div className="flex flex-wrap items-center justify-center gap-4 text-sm text-white/50">
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 rounded bg-green-500/20 border border-green-500/30" />
              <span>{w.legendWinner}</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-2 h-2 rounded-full bg-primary animate-pulse" />
              <span>{liveText}</span>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-white/30">{tbd}</span>
              <span>{w.legendTbd}</span>
            </div>
          </div>
          <p className="text-center text-xs text-white/40 sm:hidden">
            {w.scrollHint}
          </p>
        </>
      )}
    </div>
  )
}

